import { useEffect, useRef, useCallback, useState, useContext } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router';
import { landingContent } from '../data/grand-slam-content';
import { LayoutContext } from '../context';
import { APICreateGrandSlamInvoice } from '../api';
import LoginPage from './LoginPage';
import './GrandSlamLanding.css';

const showcaseData = [
  { label: 'Лекции', desc: 'Смотришь лекцию - и тема встает на место. 100+ видео, где сложное становится понятным и логичным.', images: ['/screenshots/lectures1.png', '/screenshots/lecture2.png', '/screenshots/lecture3.png'] },
  { label: 'PDF', desc: 'Структурированные слайды к каждой лекции. Информация разложена так, что четко укладывается в голове.', images: ['/screenshots/pdf1.png', '/screenshots/pdf2.png', '/screenshots/pdf3.png', '/screenshots/pdf4.png', '/screenshots/pdf5.png', '/screenshots/pdf6.png', '/screenshots/pdf7.png'] },
  { label: 'Anki', desc: 'Делать карточки самому - долго и сложно. APEX позаботился об этом. Алгоритм сам напомнит то, что ты начал забывать.', images: ['/screenshots/anki1.1.png', '/screenshots/anki2.png', '/screenshots/anki2.1.png'] },
  { label: 'Мини-тесты', desc: '5 000+ вопросов (EN + RU), которые идеально помогут запомнить трудные факты и закрепить материал после каждой темы.', images: ['/screenshots/mini-test1.png', '/screenshots/mini-test2.png', '/screenshots/mini-test3.png', '/screenshots/mini-test4.png'] },
  { label: 'Клин. задачи', desc: 'Пациент, симптомы, решение (EN + RU). Каждая задача тренирует клиническое мышление и глубокое понимание патофизиологии.', images: ['/screenshots/clinical1.png', '/screenshots/clinical2.png', '/screenshots/clinical3.png'] },
  { label: 'Study Plan', desc: 'Открыл план - видишь что делать сегодня. Все готово: берешь и делаешь. Без гадания, без хаоса.', images: ['/screenshots/studyplan1.png', '/screenshots/studyplan2.png', '/screenshots/studyplan3.png'] },
  { label: 'Библиотека', desc: '1 200+ концептов из First Aid на английском. Вся теория Step 1 в одном месте - читай, закрепляй, возвращайся.', images: ['/screenshots/platform3.png', '/screenshots/platform4.png', '/screenshots/platform5.png'] },
  { label: 'Платформа', desc: 'Тесты, библиотека, прогресс, статистика (EN + RU). Одно место вместо десяти вкладок.', images: ['/screenshots/platform1.png', '/screenshots/platform2.png', '/screenshots/platform6.png'] },
];

function ProductShowcase() {
  const [activeTab, setActiveTab] = useState(0);
  const scrollRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const current = showcaseData[activeTab];

  useEffect(() => {
    setActiveSlide(0);
    if (scrollRef.current) scrollRef.current.scrollTo({ left: 0, behavior: 'instant' });
  }, [activeTab]);

  // Track which slide is in view via scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const scrollLeft = el.scrollLeft;
      const cardWidth = el.querySelector('.gs-showcase-card')?.offsetWidth || 1;
      const gap = 16;
      const idx = Math.round(scrollLeft / (cardWidth + gap));
      setActiveSlide(Math.min(idx, current.images.length - 1));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [activeTab, current.images.length]);

  const scrollToSlide = (idx) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelectorAll('.gs-showcase-card')[idx];
    if (card) card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  return (
    <section style={{ padding: '96px 0', background: 'var(--gs-grey-50)' }}>
      <div className="gs-shell" style={{ textAlign: 'center' }}>
        <p className="gs-eyebrow">Как это выглядит</p>
        <h2 className="gs-headline-section gs-gradient-text" style={{ margin: '16px auto 0' }}>Внутри системы</h2>

        {/* Tabs */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '40px', flexWrap: 'wrap' }}>
          {showcaseData.map((item, i) => (
            <button key={item.label} onClick={() => setActiveTab(i)} style={{
              padding: '8px 20px', borderRadius: '980px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
              border: 'none',
              background: i === activeTab ? 'var(--gs-grey-900)' : 'transparent',
              color: i === activeTab ? '#fff' : 'var(--gs-grey-500)',
              transition: 'all 0.25s ease',
            }}>
              {item.label}
            </button>
          ))}
        </div>

      </div>

      {/* Horizontal swipe strip */}
      <div
        ref={scrollRef}
        className="gs-showcase-scroll"
      >
        <div className="gs-showcase-track">
          {current.images.map((src, i) => (
            <div key={`${activeTab}-${i}`} className="gs-showcase-card">
              <div className="gs-showcase-card__img">
                <img
                  src={src}
                  alt={`${current.label} ${i + 1}`}
                  draggable={false}
                />
              </div>
              <p className="gs-showcase-card__text">
                <span style={{ fontWeight: 700 }}>{current.label}.</span>{' '}{current.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Dots */}
      {current.images.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
          {current.images.map((_, i) => (
            <button key={i} onClick={() => scrollToSlide(i)} style={{
              width: i === activeSlide ? '24px' : '8px', height: '8px',
              borderRadius: '4px', border: 'none', cursor: 'pointer',
              background: i === activeSlide ? 'var(--gs-grey-900)' : 'var(--gs-grey-300)',
              transition: 'all 0.3s ease',
            }} />
          ))}
        </div>
      )}
    </section>
  );
}

function renderCheck(val, isApex) {
  if (typeof val === 'string') return <span className="gs-compare__text">{val}</span>;
  if (val === true) return <span className={`gs-compare__yes ${isApex ? 'gs-compare__yes--apex' : 'gs-compare__yes--other'}`}>&#10003;</span>;
  if (val === false) return <span className="gs-compare__no">&#10007;</span>;
  return <span className="gs-compare__partial">&#9675;</span>;
}

export default function GrandSlamLandingPage() {
  const navigate = useNavigate();
  const revealRefs = useRef([]);
  const { token } = useContext(LayoutContext) || {};
  const [showAuth, setShowAuth] = useState(false);
  const [authTab, setAuthTab] = useState(1);
  const [payLoading, setPayLoading] = useState(null);

  const handlePay = async (period) => {
    if (!token) {
      localStorage.setItem('pending_plan', String(period));
      setAuthTab(1);
      setShowAuth(true);
      return;
    }
    setPayLoading(period);
    try {
      const data = await APICreateGrandSlamInvoice(token, period);
      if (data.payment_url) {
        window.location.href = data.payment_url;
      }
    } catch (e) {
      alert('Ошибка создания платежа. Попробуйте позже.');
    }
    setPayLoading(null);
  };

  const openRegister = () => { setAuthTab(1); setShowAuth(true); };

  const addRevealRef = useCallback((el) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('gs-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

    revealRefs.current.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  if (showAuth) {
    return <LoginPage defaultTab={authTab} onBack={() => setShowAuth(false)} />;
  }

  const { about, agitation, comparison, components, faq, finalCta, guarantee, hero, nav, pain, pricing, solution, testimonials, valueStack } = landingContent;

  return (
    <div className="gs-page">

      {/* ═══ NAV — floating, minimal ═══ */}
      <header style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(251,251,253,0.72)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', borderBottom: '1px solid var(--gs-grey-100)' }}>
        <div className="gs-shell-wide" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '52px' }}>
          <RouterLink to="/" style={{ fontWeight: 800, fontSize: '1.05rem', letterSpacing: '-0.02em', color: 'var(--gs-grey-900)', textDecoration: 'none' }}>
            APEX
          </RouterLink>
          <nav className="gs-hide-mobile" style={{ display: 'flex', gap: '28px' }}>
            {nav.map(item => (
              <a key={item.href} href={item.href} style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--gs-grey-500)', textDecoration: 'none' }}
                 onMouseEnter={e => e.target.style.color = 'var(--gs-grey-900)'}
                 onMouseLeave={e => e.target.style.color = 'var(--gs-grey-500)'}>
                {item.label}
              </a>
            ))}
          </nav>
          <a href="#pricing" className="gs-pill gs-pill-dark" style={{ padding: '8px 20px', fontSize: '0.8rem' }}>
            Начать
          </a>
        </div>
      </header>

      {/* ═══ HERO — white with blue glow ═══ */}
      <section style={{ paddingTop: '96px', paddingBottom: '96px', position: 'relative', overflow: 'hidden', background: '#fff' }}>
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: 'linear-gradient(to bottom, #D3E8F4, #D3E8F4 45%, rgba(211,232,244,0.4) 65%, #ffffff 85%)',
          pointerEvents: 'none',
        }} />
        <div className="gs-shell" style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <p className="gs-eyebrow gs-rise" style={{ display: 'inline-block', marginBottom: '8px' }}>{hero.badge}</p>
          <h1 className="gs-headline-hero gs-gradient-text gs-rise gs-d1" style={{ maxWidth: '720px', margin: '20px auto 0' }}>
            Сдай USMLE Step&nbsp;1 с первой попытки
          </h1>
          <p className="gs-body gs-muted gs-rise gs-d2" style={{ maxWidth: '520px', margin: '20px auto 0' }}>
            Знания мирового уровня на русском языке. Пошаговый план, видеолекции, Anki, тесты и клинические задачи. От доктора, который набрал 260 и прошел Match.
          </p>
          <div className="gs-rise gs-d3" style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '36px', flexWrap: 'wrap' }}>
            <button onClick={openRegister} className="gs-pill gs-pill-primary" style={{ padding: '12px 28px', fontSize: '0.88rem', cursor: 'pointer', border: 'none' }}>Начать подготовку →</button>
            <a href="#system" className="gs-pill gs-pill-secondary" style={{ padding: '12px 28px', fontSize: '0.88rem' }}>Что входит</a>
          </div>
          {/* Stats strip */}
          <div className="gs-hero-stats">
            {[
              ['120+', 'видеолекций'],
              ['5,500+', 'тестов'],
              ['2,800+', 'клин. задач'],
              ['12,500', 'Anki-карточек'],
            ].map(([num, label], i) => (
              <div key={num} className="gs-hero-stat">
                <p className="gs-hero-stat__num">{num}</p>
                <p className="gs-hero-stat__label">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="gs-divider" />

      {/* ═══ PAIN — grey ═══ */}
      <section style={{ padding: '96px 0', background: 'var(--gs-grey-50)' }}>
        <div ref={addRevealRef} className="gs-reveal gs-shell" style={{ textAlign: 'center', marginBottom: '56px' }}>
          <p className="gs-eyebrow">{pain.eyebrow}</p>
          <h2 className="gs-headline-section gs-gradient-text" style={{ maxWidth: '640px', margin: '16px auto 0' }}>{pain.title}</h2>
        </div>
        <div className="gs-shell-wide">
          <div ref={addRevealRef} className="gs-reveal gs-grid-responsive" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            {pain.items.map(item => (
              <div key={item.title} className="gs-card gs-card-shimmer" style={{ padding: '28px 24px' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '10px' }}>{item.title}</h3>
                <p className="gs-body-small" style={{ color: 'var(--gs-grey-700)', lineHeight: 1.6 }}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ AGITATION — dark, centered layout ═══ */}
      <section className="gs-section-dark gs-dark-animated" style={{ padding: '96px 0' }}>
        <div className="gs-shell" style={{ textAlign: 'center' }}>
          <h2 className="gs-headline-section gs-text-gradient-dark" style={{ maxWidth: '640px', margin: '0 auto' }}>{agitation.title}</h2>
          <p className="gs-body" style={{ color: 'rgba(255,255,255,0.50)', maxWidth: '520px', margin: '16px auto 0' }}>{agitation.description}</p>
          <div className="gs-grid-responsive" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginTop: '48px' }}>
            {agitation.outcomes.map(outcome => (
              <div key={outcome} className="gs-card" style={{ padding: '24px', textAlign: 'left' }}>
                <p className="gs-body-small" style={{ color: 'rgba(255,255,255,0.82)' }}>{outcome}</p>
              </div>
            ))}
          </div>
          <p style={{ marginTop: '32px', fontSize: '0.95rem', fontWeight: 600, color: 'var(--gs-terra)' }}>{agitation.note}</p>
        </div>
      </section>

      {/* ═══ SOLUTION — grey ═══ */}
      <section id="system" style={{ padding: '96px 0', background: 'var(--gs-grey-50)' }}>
        <div ref={addRevealRef} className="gs-reveal gs-shell" style={{ textAlign: 'center', marginBottom: '56px' }}>
          <p className="gs-eyebrow">Решение</p>
          <h2 className="gs-headline-section gs-gradient-text" style={{ maxWidth: '540px', margin: '16px auto 0' }}>{solution.title}</h2>
          <p className="gs-body gs-muted" style={{ maxWidth: '480px', margin: '16px auto 0' }}>{solution.description}</p>
        </div>
        <div className="gs-shell">
          <div className="gs-grid-responsive" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            {solution.pillars.map(pillar => (
              <div key={pillar} className="gs-card" style={{ padding: '32px 28px' }}>
                <p style={{ fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.45 }}>{pillar}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ COMPONENTS — white ═══ */}
      <section style={{ padding: '96px 0', background: '#fff' }}>
        <div className="gs-shell" style={{ textAlign: 'center', marginBottom: '56px' }}>
          <p className="gs-eyebrow">Компоненты</p>
          <h2 className="gs-headline-section gs-gradient-text" style={{ maxWidth: '480px', margin: '16px auto 0' }}>Что входит в систему</h2>
        </div>
        <div className="gs-shell-wide">
          <div className="gs-grid-responsive" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            {components.map(item => (
              <div key={item.title} className="gs-card" style={{ padding: '28px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                  <div>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>{item.title}</h3>
                    <p className="gs-body-small gs-muted" style={{ marginTop: '2px' }}>{item.subtitle}</p>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <span style={{ fontSize: '0.72rem', color: 'var(--gs-grey-500)', textDecoration: 'line-through' }}>{item.oldPrice}</span>
                    <p style={{ fontSize: '0.7rem', fontWeight: 600, color: '#2d7ab3', marginTop: '2px' }}>Входит в систему</p>
                  </div>
                </div>
                <p className="gs-body-small gs-muted" style={{ marginTop: '14px' }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRODUCT SHOWCASE — slider ═══ */}
      <ProductShowcase />

      {/* ═══ STUDY PLAN — Apple two-column feature ═══ */}
      <section style={{ padding: '96px 0', background: '#fff', overflow: 'hidden' }}>
        <div className="gs-shell" style={{ textAlign: 'center', marginBottom: '56px' }}>
          <p className="gs-eyebrow">Study Plan</p>
          <h2 className="gs-headline-section gs-gradient-text" style={{ maxWidth: '620px', margin: '16px auto 0' }}>
            Единственный пошаговый план подготовки к Step 1
          </h2>
          <p className="gs-body gs-muted" style={{ maxWidth: '480px', margin: '14px auto 0' }}>
            Не расписание и не чеклист. Полноценная система, где каждый день расписан до минуты - что читать, что смотреть, что решать. Открыл - сделал - пошёл дальше.
          </p>
        </div>

        <div className="gs-shell-wide">
          <div className="gs-plan-layout">
            {/* Left — text + features */}
            <div className="gs-plan-left">
              <div className="gs-plan-left__features">
                {[
                  { title: 'Революционный формат', desc: 'Ни один русскоязычный ресурс не даёт готовый маршрут. APEX - первый и единственный' },
                  { title: 'Контрольные точки', desc: 'Система не пускает дальше, пока не закреплён предыдущий блок. Без пробелов в знаниях' },
                  { title: 'Клинические задачи', desc: 'В конце каждого блока - контрольные задачи, которые открывают доступ к следующему дню' },
                  { title: 'Закрытый чат с ментором', desc: 'Прямой доступ к преподавателю и комьюнити студентов которые идут тем же путём' },
                ].map(f => (
                  <div key={f.title} className="gs-plan-feat">
                    <h3 className="gs-plan-feat__title">{f.title}</h3>
                    <p className="gs-plan-feat__desc">{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Day 1 UI card (dark, like the real app) */}
            <div className="gs-plan-right">
              <div className="gs-plan-card">
                <div className="gs-plan-card__header">
                  <div>
                    <p className="gs-plan-card__day">День 1</p>
                    <p className="gs-plan-card__topic">Молекулярная биология, часть 1</p>
                  </div>
                  <span className="gs-plan-card__count">0/5</span>
                </div>
                <div className="gs-plan-card__steps">
                  {[
                    { num: '1', action: 'Прочитай текстовый модуль', items: ['DNA structure', 'Chromatin', 'DNA replication', 'DNA mutations', 'DNA repair'], time: '15 мин' },
                    { num: '2', action: 'Закрепи мини-тестами', detail: 'Molecular Biology (5-10 вопросов)', time: '10 мин' },
                    { num: '3', action: 'Посмотри видеолекцию', detail: 'Molecular Biology, Part 1', time: '25 мин' },
                    { num: '4', action: 'Пройди Anki-карточки', detail: 'Колода Molecular Biology', time: '20 мин' },
                    { num: '5', action: 'Контрольный тест', detail: 'QBank, 20 вопросов, порог 60%', time: '30 мин', isGate: true },
                  ].map(step => (
                    <div key={step.num} className={`gs-plan-card__step ${step.isGate ? 'gs-plan-card__step--gate' : ''}`}>
                      <span className={`gs-plan-card__num ${step.isGate ? 'gs-plan-card__num--gate' : ''}`}>{step.num}</span>
                      <div className="gs-plan-card__step-body">
                        <p className="gs-plan-card__action">{step.action}</p>
                        {step.items && (
                          <div className="gs-plan-card__tags">
                            {step.items.map(item => <span key={item} className="gs-plan-card__tag">{item}</span>)}
                          </div>
                        )}
                        {step.detail && <p className="gs-plan-card__detail">{step.detail}</p>}
                      </div>
                      <span className="gs-plan-card__time">{step.time}</span>
                    </div>
                  ))}
                </div>
                {/* Collapsed days peek */}
                <div className="gs-plan-card__peek">
                  {['День 2', 'День 3', 'День 4', '...'].map(d => (
                    <span key={d} className="gs-plan-card__peek-day">{d}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ VALUE STACK — dark, Apple style ═══ */}
      <section id="value" className="gs-section-dark" style={{ padding: '96px 0' }}>
        <div className="gs-shell" style={{ textAlign: 'center' }}>
          <h2 className="gs-headline-section gs-text-gradient-rainbow" style={{ maxWidth: '400px', margin: '0 auto' }}>{valueStack.title}</h2>
          <div style={{ maxWidth: '360px', margin: '14px auto 0' }}>
            {valueStack.description.map((line, i) => (
              <p key={i} className="gs-body" style={{ color: 'rgba(255,255,255,0.5)', margin: 0 }}>{line}</p>
            ))}
          </div>

          {/* 3-col grid of items */}
          <div className="gs-value-grid gs-value-grid--dark">
            {valueStack.items.map(item => (
              <div key={item.text} className="gs-value-item">
                <p className="gs-value-item__text">{item.text}</p>
                <p className="gs-value-item__note">{item.note}</p>
              </div>
            ))}
          </div>

          {/* Price */}
          <div style={{ marginTop: '56px' }}>
            <span style={{ fontSize: '1.1rem', fontWeight: 600, color: 'rgba(255,255,255,0.25)', textDecoration: 'line-through' }}>{valueStack.oldPrice}</span>
            <p style={{ fontSize: 'clamp(2.4rem, 5vw, 3.2rem)', fontWeight: 800, letterSpacing: '-0.04em', marginTop: '4px', color: '#fff' }}>{valueStack.price}</p>
          </div>

          <a href="#pricing" className="gs-pill gs-pill-blue" style={{ marginTop: '24px', display: 'inline-block', padding: '14px 32px', fontSize: '0.88rem' }}>
            {valueStack.cta} →
          </a>
        </div>
      </section>

      {/* ═══ ABOUT — centered, Apple style ═══ */}
      <section style={{ padding: '96px 0', background: 'var(--gs-grey-50)' }}>
        <div className="gs-shell" style={{ textAlign: 'center' }}>
          <p className="gs-eyebrow">Об авторе</p>
          <h2 className="gs-headline-section gs-gradient-text" style={{ margin: '16px auto 0' }}>{about.title}</h2>
          <div style={{ maxWidth: '520px', margin: '20px auto 0', display: 'flex', flexDirection: 'column', gap: '12px', textAlign: 'left' }}>
            {about.story.map((p, i) => (
              <p key={i} style={{
                fontSize: '0.95rem', lineHeight: 1.65, margin: 0,
                color: i === about.story.length - 1 ? 'var(--gs-grey-900)' : 'var(--gs-grey-700)',
                fontWeight: i === about.story.length - 1 ? 700 : 400,
              }}>{p}</p>
            ))}
          </div>

          {/* Metrics strip */}
          <div className="gs-about-metrics">
            {about.metrics.map((m, i) => (
              <div key={m.label} className="gs-about-metric">
                <p className="gs-about-metric__label">{m.label}</p>
                <p className="gs-about-metric__value">{m.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ COMPARISON TABLE — dark ═══ */}
      <section className="gs-section-dark" style={{ padding: '96px 0' }}>
        <div className="gs-shell" style={{ textAlign: 'center', marginBottom: '56px' }}>
          <p className="gs-eyebrow" style={{ color: 'var(--gs-terra)' }}>Сравнение</p>
          <h2 className="gs-headline-section gs-text-gradient-dark" style={{ maxWidth: '640px', margin: '16px auto 0' }}>{comparison.title}</h2>
          <p className="gs-body" style={{ color: 'rgba(255,255,255,0.5)', maxWidth: '400px', margin: '14px auto 0' }}>{comparison.description}</p>
        </div>
        <div className="gs-shell-wide">
          <div className="gs-compare-wrap">
            <table className="gs-compare">
              <thead>
                <tr>
                  <th className="gs-compare__feature-head">Что получает студент</th>
                  <th className="gs-compare__apex-head">
                    <span className="gs-compare__apex-badge">Обучающая платформа</span>
                    APEX
                  </th>
                  {['UWorld', 'AMBOSS', 'Другие курсы'].map(col => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="gs-compare__type-row">
                  <td>Тип платформы</td>
                  <td className="gs-compare__apex-col">
                    <span className="gs-compare__type-apex">{comparison.apexType}</span>
                  </td>
                  {comparison.otherTypes.map(t => (
                    <td key={t}><span className="gs-compare__type-other">{t}</span></td>
                  ))}
                </tr>
                {comparison.rows.map((row, i) => (
                  <tr key={i}>
                    <td>{row.feature}</td>
                    <td className="gs-compare__apex-col">{renderCheck(row.apex, true)}</td>
                    <td>{renderCheck(row.uworld, false)}</td>
                    <td>{renderCheck(row.amboss, false)}</td>
                    <td>{renderCheck(row.unis, false)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS — PASS results ═══ */}
      <section id="proof" style={{ padding: '96px 0', background: '#fff' }}>
        <div className="gs-shell" style={{ textAlign: 'center', marginBottom: '40px' }}>
          <p className="gs-eyebrow">Результаты</p>
          <h2 className="gs-headline-section gs-gradient-text" style={{ maxWidth: '560px', margin: '16px auto 0' }}>Наши студенты сдают</h2>
          <p className="gs-body gs-muted" style={{ maxWidth: '400px', margin: '14px auto 0' }}>Реальные Score Reports студентов APEX</p>
        </div>
        <div className="gs-pass-scroll">
          <div className="gs-pass-track">
            {[1,2,3,4,5,6,7,8,9].map(n => (
              <div key={n} className="gs-pass-card">
                <img
                  src={`/screenshots/pass${n}.jpg`}
                  alt={`USMLE PASS ${n}`}
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ GUARANTEE — grey ═══ */}
      <section style={{ padding: '96px 0', background: 'var(--gs-grey-50)' }}>
        <div className="gs-shell" style={{ textAlign: 'center' }}>
          <p className="gs-eyebrow">Гарантия</p>
          <h2 className="gs-headline-section" style={{ maxWidth: '580px', margin: '16px auto 0' }}>{guarantee.title}</h2>
          <p className="gs-body gs-muted" style={{ maxWidth: '480px', margin: '16px auto 0' }}>{guarantee.intro}</p>
          <div className="gs-grid-responsive" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', maxWidth: '720px', margin: '40px auto 0' }}>
            {guarantee.conditions.map(c => (
              <div key={c} className="gs-card" style={{ padding: '18px 20px', textAlign: 'left' }}>
                <p className="gs-body-small">{c}</p>
              </div>
            ))}
          </div>
          <p style={{ marginTop: '28px', fontSize: '0.95rem', fontWeight: 700, color: 'var(--gs-accent-1)' }}>{guarantee.outro}</p>
        </div>
      </section>

      {/* ═══ PRICING — dark, Apple style ═══ */}
      <section id="pricing" className="gs-section-dark" style={{ padding: '96px 0' }}>
        <div className="gs-shell" style={{ textAlign: 'center', marginBottom: '56px' }}>
          <p className="gs-eyebrow" style={{ color: 'var(--gs-terra)' }}>Тарифы</p>
          <h2 className="gs-headline-section gs-text-gradient-rainbow" style={{ maxWidth: '480px', margin: '16px auto 0' }}>{pricing.title}</h2>
          <p className="gs-body" style={{ maxWidth: '360px', margin: '14px auto 0', color: 'rgba(255,255,255,0.5)' }}>{pricing.trial}</p>
          <button onClick={openRegister} className="gs-pill gs-pill-blue" style={{ marginTop: '20px', display: 'inline-block', padding: '10px 24px', fontSize: '0.8rem', cursor: 'pointer', border: 'none' }}>
            Попробовать бесплатно
          </button>
        </div>
        <div className="gs-shell">
          <div className="gs-pricing-grid">
            {pricing.plans.map((plan, planIdx) => {
              const featured = plan.featured;
              const periodMap = [1, 3, 6, 12];
              const period = periodMap[planIdx];
              return (
                <div key={plan.name} className={`gs-price-card ${featured ? 'gs-price-card--featured' : ''}`}>
                  {plan.badge && (
                    <span className="gs-price-card__badge">{plan.badge}</span>
                  )}
                  <p className="gs-price-card__name">{plan.name}</p>
                  <div className="gs-price-card__price-row">
                    {plan.oldPrice && <span className="gs-price-card__old-price">{plan.oldPrice}</span>}
                    <span className="gs-price-card__price">{plan.price}</span>
                    {plan.saving && <span className="gs-price-card__saving">{plan.saving}</span>}
                  </div>
                  <p className="gs-price-card__per-month">{plan.perMonth}</p>
                  <p className="gs-price-card__desc">{plan.description}</p>
                  <button
                    onClick={() => handlePay(period)}
                    disabled={payLoading === period}
                    className={`gs-pill ${featured ? 'gs-pill-blue' : 'gs-pill-dark'}`}
                    style={{ width: '100%', padding: '12px 20px', fontSize: '0.82rem', marginTop: 'auto', cursor: 'pointer', opacity: payLoading === period ? 0.6 : 1 }}>
                    {payLoading === period ? 'Загрузка...' : 'Оплатить'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ FAQ — grey ═══ */}
      <section id="faq" style={{ padding: '96px 0', background: 'var(--gs-grey-50)' }}>
        <div className="gs-shell" style={{ textAlign: 'center', marginBottom: '56px' }}>
          <p className="gs-eyebrow">FAQ</p>
          <h2 className="gs-headline-section">Частые вопросы</h2>
        </div>
        <div className="gs-shell" style={{ maxWidth: '680px' }}>
          {faq.map(item => (
            <details key={item.question} style={{ borderBottom: '1px solid var(--gs-grey-100)' }}>
              <summary style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', padding: '20px 0', fontSize: '1rem', fontWeight: 600, color: 'var(--gs-grey-900)' }}>
                <span>{item.question}</span>
                <span className="gs-faq-icon" style={{ color: 'var(--gs-accent-1)', fontSize: '1.3rem', flexShrink: 0, transition: 'transform 0.25s' }}>+</span>
              </summary>
              <p className="gs-body-small gs-muted" style={{ paddingBottom: '20px', paddingRight: '40px' }}>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ═══ FINAL CTA — dark, dramatic, animated bg ═══ */}
      <section className="gs-section-dark gs-dark-animated" style={{ padding: '120px 0', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div className="gs-hero-orb" style={{ width: '500px', height: '500px', background: 'rgba(30,58,95,0.08)', top: '-150px', right: '-150px', position: 'absolute', borderRadius: '50%', filter: 'blur(80px)', pointerEvents: 'none', animation: 'gs-orb-float 10s ease-in-out infinite' }} />
        <div className="gs-shell">
          <h2 className="gs-headline-hero gs-text-gradient-dark" style={{ maxWidth: '640px', margin: '0 auto' }}>{finalCta.title}</h2>
          <p className="gs-body" style={{ color: 'rgba(255,255,255,0.56)', maxWidth: '520px', margin: '20px auto 0' }}>{finalCta.description}</p>
          {/* Stats strip */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', marginTop: '40px', flexWrap: 'wrap' }}>
            {[
              ['120+', 'лекций'],
              ['5,500+', 'тестов'],
              ['2,800+', 'клин. задач'],
              ['12,500', 'Anki-карточек'],
            ].map(([num, label]) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '1.8rem', fontWeight: 800, letterSpacing: '-0.03em', color: '#fff', lineHeight: 1 }}>{num}</p>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.4)', marginTop: '6px' }}>{label}</p>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '36px', flexWrap: 'wrap' }}>
            <button onClick={openRegister} className="gs-pill gs-pill-blue" style={{ padding: '14px 32px', fontSize: '0.88rem', cursor: 'pointer', border: 'none' }}>Попробовать бесплатно</button>
            <a href="#pricing" className="gs-pill gs-pill-ghost" style={{ padding: '14px 32px', fontSize: '0.88rem' }}>Посмотреть тарифы</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: '32px 0', textAlign: 'center', background: 'var(--gs-black)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.32)' }}>APEX Med Academy, {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}
